import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { CreditCard, Landmark, Loader2, ArrowRight, Copy, Check } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BANK = {
  name: "Walterstown GFC",
  bank: "Allied Irish Banks (AIB)",
  iban: "IE22AIBK93239652141237",
  bic: "AIBKIE2D",
  ref: "BALLWALL + Your Name",
};

const CopyRow = ({ label, value, id }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    toast.success(`${label} copied`);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="flex items-center justify-between border-b border-white/15 py-4">
      <div>
        <p className="label-caps mb-1">{label}</p>
        <p className="font-oswald text-lg tracking-wide text-white">{value}</p>
      </div>
      <button
        data-testid={`copy-${id}-btn`}
        onClick={copy}
        className="text-white/50 transition-colors hover:text-white"
        aria-label={`Copy ${label}`}
      >
        {copied ? <Check className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
      </button>
    </div>
  );
};

export default function DonationForm() {
  const [tab, setTab] = useState("card");
  const [form, setForm] = useState({
    donor_name: "",
    business_name: "",
    address: "",
    email: "",
    phone: "",
    plaque_message: "",
    tax_relief_consent: false,
  });
  const [bankConsent, setBankConsent] = useState(false);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const TAX_CONSENT_TEXT =
    "As a PAYE/self-assessed taxpayer, I consent to Walterstown GFC claiming tax relief on my donation, and understand the club will contact me to complete the relevant form when required.";

  const TAX_INTRO_TEXT =
    "The Club may be able to claim additional tax relief on your €1,000 donation at no extra cost to you, under Revenue Tax Relief for Donation Scheme. Tick the check box below if you agree to this.";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.donor_name || !form.address || !form.email || !form.phone || !form.plaque_message) {
      toast.error("Please complete all required fields.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/ballwall/checkout`, {
        ...form,
        origin_url: window.location.origin,
      });
      // Break out of any parent iframe (Stripe Checkout refuses to load framed)
      try {
        (window.top || window).location.href = data.checkout_url;
      } catch (_) {
        window.location.href = data.checkout_url;
      }
    } catch (err) {
      console.error(err);
      toast.error("Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-none border-0 border-b border-white/25 bg-transparent px-0 py-3 font-inter text-lg text-white placeholder:text-white/50 focus:border-white focus:outline-none focus:ring-0 transition-colors";

  return (
    <div className="border border-white/20 bg-[#050505]">
      {/* Tabs */}
      <div className="grid grid-cols-2 border-b border-white/20">
        {[
          { id: "card", label: "Card / Stripe", icon: CreditCard },
          { id: "bank", label: "Bank Transfer", icon: Landmark },
        ].map((t) => (
          <button
            key={t.id}
            data-testid={`tab-${t.id}-btn`}
            onClick={() => setTab(t.id)}
            className={`relative flex items-center justify-center gap-2 py-5 font-oswald text-sm uppercase tracking-[0.2em] transition-colors ${
              tab === t.id ? "text-black" : "text-white/50 hover:text-white"
            }`}
          >
            {tab === t.id && (
              <motion.span
                layoutId="don-tab-bg"
                className="absolute inset-0 bg-white"
                transition={{ type: "spring", stiffness: 400, damping: 35 }}
              />
            )}
            <span className="relative z-10 flex items-center gap-2">
              <t.icon className="h-4 w-4" /> {t.label}
            </span>
          </button>
        ))}
      </div>

      <div className="p-8 md:p-12">
        <div className="mb-10 border-b border-white/15 pb-6">
          <p className="label-caps mb-2">Your Contribution</p>
          <p className="font-oswald text-6xl font-bold leading-none text-white md:text-7xl">
            €1,000
          </p>
        </div>

        {tab === "card" ? (
          <form onSubmit={submit} className="space-y-8" data-testid="donation-form">
            <div>
              <label className="label-caps mb-3 block">Your Name</label>
              <input
                data-testid="donor-name-input"
                value={form.donor_name}
                onChange={update("donor_name")}
                placeholder="e.g. John Murphy"
                className={inputCls}
              />
            </div>
            <div>
              <label className="label-caps mb-3 block">Business Name (if relevant)</label>
              <input
                data-testid="business-name-input"
                value={form.business_name}
                onChange={update("business_name")}
                placeholder="Optional"
                className={inputCls}
              />
            </div>
            <div>
              <label className="label-caps mb-3 block">Your Address</label>
              <input
                data-testid="address-input"
                value={form.address}
                onChange={update("address")}
                placeholder="Street, town, county, Eircode"
                className={inputCls}
              />
            </div>
            <div>
              <label className="label-caps mb-3 block">Email Address</label>
              <input
                data-testid="donor-email-input"
                type="email"
                value={form.email}
                onChange={update("email")}
                placeholder="you@example.com"
                className={inputCls}
              />
            </div>
            <div>
              <label className="label-caps mb-3 block">Phone Number</label>
              <input
                data-testid="phone-input"
                type="tel"
                value={form.phone}
                onChange={update("phone")}
                placeholder="e.g. 087 123 4567"
                className={inputCls}
              />
            </div>
            <div>
              <label className="label-caps mb-3 block">Plaque Message / Name</label>
              <textarea
                data-testid="plaque-message-input"
                value={form.plaque_message}
                onChange={update("plaque_message")}
                rows={2}
                maxLength={200}
                placeholder="Exactly as you want it engraved on the wall"
                className={`${inputCls} resize-none`}
              />
              <p className="mt-2 text-right text-xs text-white/55">
                {form.plaque_message.length}/200
              </p>
              <p className="mt-1 text-xs leading-relaxed text-white/50" data-testid="plaque-text-only-note">
                Please note: plaque messages are text only — we're unable to include business logos,
                images or graphics.
              </p>
            </div>

            <p className="font-inter text-sm leading-relaxed text-white/75" data-testid="tax-relief-intro-card">
              {TAX_INTRO_TEXT}
            </p>
            <label
              data-testid="tax-relief-consent-card"
              className="flex cursor-pointer items-start gap-3 border border-white/15 p-4 transition-colors hover:border-white/30"            >
              <input
                data-testid="tax-relief-consent-card-checkbox"
                type="checkbox"
                checked={form.tax_relief_consent}
                onChange={(e) =>
                  setForm((f) => ({ ...f, tax_relief_consent: e.target.checked }))
                }
                className="mt-1 h-4 w-4 flex-shrink-0 accent-white"
              />
              <span className="font-inter text-sm leading-relaxed text-white/75">
                {TAX_CONSENT_TEXT}
              </span>
            </label>

            <button
              data-testid="donate-card-btn"
              type="submit"
              disabled={loading}
              className="group flex w-full items-center justify-between rounded-none bg-white px-8 py-6 font-oswald text-base uppercase tracking-[0.2em] text-black transition-colors hover:bg-white/80 disabled:opacity-60"
            >
              {loading ? (
                <span className="mx-auto flex items-center gap-3">
                  <Loader2 className="h-5 w-5 animate-spin" /> Redirecting…
                </span>
              ) : (
                <>
                  <span>Donate €1,000 Securely</span>
                  <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                </>
              )}
            </button>
            <p className="text-center text-xs text-white/60">
              Secure payment powered by Stripe
            </p>
          </form>
        ) : (
          <div data-testid="bank-transfer-panel">
            <p className="mb-6 text-white/80">
              Prefer a direct transfer? Send €1,000 using the details below, then email your
              plaque message to the treasurer so we can reserve your plaque.
            </p>
            <p className="mb-6 text-xs leading-relaxed text-white/50" data-testid="plaque-text-only-note-bank">
              Please note: plaque messages are text only — we're unable to include business logos,
              images or graphics.
            </p>
            <CopyRow label="Account Name" value={BANK.name} id="name" />
            <CopyRow label="Bank" value={BANK.bank} id="bank" />
            <CopyRow label="IBAN" value={BANK.iban} id="iban" />
            <CopyRow label="BIC" value={BANK.bic} id="bic" />
            <CopyRow label="Reference" value={BANK.ref} id="ref" />

            <p className="mt-6 font-inter text-sm leading-relaxed text-white/75" data-testid="tax-relief-intro-bank">
              {TAX_INTRO_TEXT}
            </p>
            <label
              data-testid="tax-relief-consent-bank"
              className="mt-3 flex cursor-pointer items-start gap-3 border border-white/15 p-4 transition-colors hover:border-white/30"
            >
              <input
                data-testid="tax-relief-consent-bank-checkbox"
                type="checkbox"
                checked={bankConsent}
                onChange={(e) => setBankConsent(e.target.checked)}
                className="mt-1 h-4 w-4 flex-shrink-0 accent-white"
              />
              <span className="font-inter text-sm leading-relaxed text-white/75">
                {TAX_CONSENT_TEXT}
              </span>
            </label>

            <a
              data-testid="bank-email-link"
              href={`mailto:treasurer.walterstown.meath@gaa.ie?cc=chairperson.walterstown.meath@gaa.ie&subject=Ball%20Wall%20Bank%20Transfer%20%E2%80%94%20Plaque%20Message&body=${encodeURIComponent(
                `My plaque message:\n\n\nTax relief consent: ${bankConsent ? "Yes" : "No"}`
              )}`}
              className="mt-8 flex w-full items-center justify-between border border-white/25 px-8 py-5 font-oswald text-sm uppercase tracking-[0.2em] text-white transition-colors hover:bg-white hover:text-black"
            >
              <span>Email your plaque message</span>
              <ArrowRight className="h-5 w-5" />
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
